import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Ruler } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const UnitMaster = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;

  const [units,         setUnits]         = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [showModal,     setShowModal]     = useState(false);
  const [editMode,      setEditMode]      = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors,        setErrors]        = useState({});

  const [formData, setFormData] = useState({ ItemUnitID: null, ItemUnit: '', UnitCode: '', Description: '', IsActive: true });

  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchUnits();
  }, [companyId, yearId]);

  useEffect(() => { filterUnits(); }, [searchTerm, units]);

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/UnitMaster.php');
      if (res.data.success) setUnits(res.data.data || []);
      else alert('Failed to fetch units: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching units.');
    } finally { setLoading(false); }
  };

  const filterUnits = () => {
    if (!searchTerm.trim()) { setFilteredUnits(units); return; }
    const q = searchTerm.toLowerCase();
    setFilteredUnits(units.filter(u =>
      u.ItemUnit?.toLowerCase().includes(q) ||
      u.UnitCode?.toLowerCase().includes(q) ||
      u.Description?.toLowerCase().includes(q)
    ));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.ItemUnit.trim()) errs.ItemUnit = 'Unit name is required';
    else if (formData.ItemUnit.length > 100) errs.ItemUnit = 'Unit name must be 100 characters or less';
    if (!formData.UnitCode.trim()) errs.UnitCode = 'Unit code is required';
    else if (formData.UnitCode.length > 20) errs.UnitCode = 'Unit code must be 20 characters or less';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const payload = { ...formData, UnitCode: formData.UnitCode.toUpperCase(), IsActive: formData.IsActive ? 1 : 0 };
      const res = await apiClient[editMode ? 'put' : 'post']('/UnitMaster.php', payload);
      if (res.data.success) {
        alert(editMode ? 'Unit updated successfully!' : 'Unit created successfully!');
        closeModal(); fetchUnits();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving unit: ' + (err.response?.data?.message || err.message));
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (unit) => {
    setFormData({ ItemUnitID: unit.ItemUnitID, ItemUnit: unit.ItemUnit || '', UnitCode: unit.UnitCode || '', Description: unit.Description || '', IsActive: unit.IsActive !== 0 });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the unit "${name}"? This may affect existing invoice records.`)) return;
    try {
      const res = await apiClient.delete('/UnitMaster.php', { data: { ItemUnitID: id } });
      if (res.data.success) { alert('Unit deleted successfully!'); fetchUnits(); }
      else alert('Error: ' + res.data.message);
    } catch (err) { alert('Error deleting unit: ' + (err.response?.data?.message || 'Please try again.')); }
  };

  const resetForm    = () => { setFormData({ ItemUnitID: null, ItemUnit: '', UnitCode: '', Description: '', IsActive: true }); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const inputCls = (err) =>
    `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none
    transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]
    ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;

  // ── no session ─────────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <Ruler size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Ruler size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Unit Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage measurement units used across the system</p>
          </div>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Unit
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by name, code or description..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredUnits.length} {filteredUnits.length === 1 ? 'unit' : 'units'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading units...</div>
        ) : filteredUnits.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <Ruler size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No units found</p>
            {searchTerm ? (
              <button onClick={() => setSearchTerm('')}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear search</button>
            ) : (
              <button onClick={openAddModal}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Add your first unit</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['#', 'Unit Name', 'Code', 'Description', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUnits.map((unit, idx) => (
                  <tr key={unit.ItemUnitID}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5 font-mono text-[12px] font-bold text-violet-700 bg-violet-50 w-12">
                      {idx + 1}
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-gray-800">{unit.ItemUnit}</td>
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-[12px] font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                        {unit.UnitCode}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">{unit.Description || '—'}</td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        unit.IsActive !== 0
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-400'
                          : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {unit.IsActive !== 0 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(unit)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(unit.ItemUnitID, unit.ItemUnit)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[500px] shadow-2xl border border-gray-200 overflow-hidden"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <Ruler size={18} /> {editMode ? 'Edit Unit' : 'Add New Unit'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-7">

              {/* Section label */}
              <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wide
                px-3.5 py-2.5 bg-gray-50 border border-gray-200 border-l-4 rounded-r-lg mb-5"
                style={{ borderLeftColor: NAVY }}>
                <Ruler size={13} /> Unit Information
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Unit Name <span className="text-red-500">*</span>
                  </label>
                  <input type="text" value={formData.ItemUnit}
                    onChange={e => setFormData({ ...formData, ItemUnit: e.target.value })}
                    maxLength={100} placeholder="e.g. Kilogram"
                    className={inputCls(errors.ItemUnit)} autoFocus />
                  {errors.ItemUnit && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.ItemUnit}</p>}
                </div>
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Unit Code <span className="text-red-500">*</span>
                  </label>
                  <input type="text" value={formData.UnitCode}
                    onChange={e => setFormData({ ...formData, UnitCode: e.target.value.toUpperCase() })}
                    maxLength={20} placeholder="e.g. KG"
                    className={inputCls(errors.UnitCode)} />
                  {errors.UnitCode && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.UnitCode}</p>}
                </div>
              </div>

              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Description</label>
                <input type="text" value={formData.Description}
                  onChange={e => setFormData({ ...formData, Description: e.target.value })}
                  maxLength={255} placeholder="Optional description"
                  className={inputCls(false)} />
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                  <input type="checkbox" checked={formData.IsActive}
                    onChange={e => setFormData({ ...formData, IsActive: e.target.checked })}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
              </div>

              {/* Hint box */}
              <div className="mb-6 px-4 py-3 bg-sky-50 border border-sky-200 rounded-lg">
                <p className="text-[13px] text-sky-700 m-0">
                  💡 <strong>Common Units:</strong> KG (Kilogram), MT (Metric Ton),
                  CBM (Cubic Metre), PCS (Pieces), CTN (Carton), LTR (Litre)
                </p>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default UnitMaster;